import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { contractService } from '../../services/contractService';
import disputeService from '../../services/disputeService';
import { userService } from '../../services/userService';
import { ContractWithUsers, Milestone, Contract } from '../../types/contract';
import { StatusBadge } from '../../components/Contracts/StatusBadge';
import { MilestoneTimeline } from '../../components/Contracts/MilestoneTimeline';
import { ProgressMetrics } from '../../components/Contracts/ProgressMetrics';
import { PaymentEscrowPanel } from '../../components/Contracts/PaymentEscrowPanel';
import { DeliverableSubmission } from '../../components/Contracts/DeliverableSubmission';
import { RevisionRequest } from '../../components/Contracts/RevisionRequest';
import { ContractActionModal, ContractActionType } from '../../components/Contracts/ContractActionModal';
import { Button } from '../../components/UI/Button';
import {
  contractStatusConfig,
  formatDate,
  formatDateTime,
  getUserRole,
  calculateContractProgress,
  canAcceptContract,
  canRejectContract,
  canCancelContract,
  canEditContract,
} from '../../utils/contractHelpers';
import {
  ArrowLeft,
  FileText,
  Calendar,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ExternalLink,
  MoreVertical,
  Edit,
  Trash2,
  Flag,
  MessageSquare,
  Send,
  RefreshCw,
  Download,
  Upload,
} from 'lucide-react';
import { useToast, useConfirm } from '../../components/UI/Toast';
import { DisputeModal } from '../../components/Contracts/DisputeModal';
import { Modal } from '../../components/UI/Modal';
import { CreateContractModal } from '../../components/Thread/CreateContractModal';
import { ReviewModal } from '../../components/Contracts/ReviewModal';
import { ReviewCard } from '../../components/Contracts/ReviewCard';
import { reviewService, Review } from '../../services/reviewService';
import { CreateContractInput, ContractStatus } from '../../types/contract';
import BriefDetailModal, { BriefDetailType } from '../../components/Profile/brief/BriefDetailModal';
import { briefService } from '../../services/briefService';
import { resolveAvatarUrl } from '../../constants/assets';

interface InvoiceSectionProps {
  contract: any;
  currentUserId: string;
  showToast: (msg: string, type?: 'success' | 'error' | 'info') => void;
  onUploaded: () => void;
}

const InvoiceSection: React.FC<InvoiceSectionProps> = ({ contract, currentUserId, showToast, onUploaded }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const isCreator = currentUserId === contract.creator_user_id;
  const hasInvoice = !!contract.invoice_oss_key;
  const fileName = contract.invoice_file_name || '发票';
  const uploadedAt = contract.invoice_uploaded_at;

  const handlePick = () => fileInputRef.current?.click();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      showToast('文件过大 (最大 20MB)', 'error');
      return;
    }
    const ok = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
    if (!ok) {
      showToast('只支持 PDF / JPG / PNG', 'error');
      return;
    }
    setUploading(true);
    try {
      const res = await contractService.uploadInvoice(contract.contract_id, file);
      if (res.success) {
        showToast(hasInvoice ? '发票已更新' : '发票已上传', 'success');
        onUploaded();
      } else {
        showToast((res as any).error || '上传失败', 'error');
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || '上传失败', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async () => {
    try {
      const res = await contractService.getInvoiceUrl(contract.contract_id);
      const data = (res.data as any)?.data || res.data;
      if (data?.downloadUrl) window.open(data.downloadUrl, '_blank');
      else showToast('获取发票失败', 'error');
    } catch {
      showToast('获取发票失败', 'error');
    }
  };

  // Hide entirely if no invoice and viewer is not the creator (nothing to show or do)
  if (!hasInvoice && !isCreator) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-white p-2 border border-slate-200">
            <FileText className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <div className="text-sm font-medium text-slate-900">
              发票 <span className="text-xs text-slate-400 font-normal">（可选）</span>
            </div>
            {hasInvoice && (
              <div className="text-xs text-slate-500 mt-0.5">
                <span className="text-slate-700">{fileName}</span>
                {uploadedAt ? (
                  <span className="ml-2 text-slate-400">
                    上传于 {new Date(Number(uploadedAt)).toLocaleString('zh-CN')}
                  </span>
                ) : null}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasInvoice && (
            <Button variant="outline" onClick={handleDownload} className="inline-flex items-center gap-2">
              <Download className="w-4 h-4" />
              下载
            </Button>
          )}
          {isCreator && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/jpg"
                className="hidden"
                onChange={handleFile}
              />
              <Button
                variant={hasInvoice ? 'outline' : 'primary'}
                onClick={handlePick}
                disabled={uploading}
                className="inline-flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                {uploading ? '上传中...' : hasInvoice ? '替换' : '上传发票'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ContractDetail = () => {
  const { showToast, ToastWrapper } = useToast();
  const { confirm } = useConfirm();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [contract, setContract] = useState<ContractWithUsers | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [showSignMethodModal, setShowSignMethodModal] = useState(false);

  const [activeTab, setActiveTab] = useState<'details' | 'milestones' | 'reviews' | 'disputes'>('milestones');
  const [selectedMilestone, setSelectedMilestone] = useState<Milestone | null>(null);
  const [showDeliverableModal, setShowDeliverableModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [actionType, setActionType] = useState<ContractActionType>('ACCEPT');
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewerInfo, setReviewerInfo] = useState<Record<string, { avatarUrl: string; nickname: string }>>({});
  const [selectedBrief, setSelectedBrief] = useState<BriefDetailType | null>(null);
  const settingsMenuRef = useRef<HTMLDivElement>(null);

  // Milestone extension (amendment) state
  const [pendingExtensions, setPendingExtensions] = useState<Record<string, any>>({});
  const [extensionModalMilestoneId, setExtensionModalMilestoneId] = useState<string | null>(null);
  const [extensionNewDate, setExtensionNewDate] = useState<string>('');
  const [extensionReason, setExtensionReason] = useState<string>('');
  const [extensionSubmitting, setExtensionSubmitting] = useState(false);

  // Disputes (申诉) on this contract
  const [disputes, setDisputes] = useState<any[]>([]);
  const [disputesLoading, setDisputesLoading] = useState(false);

  // Close settings menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target as Node)) {
        setShowSettingsMenu(false);
      }
    };

    if (showSettingsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettingsMenu]);

  // Fetch Contract and User Data
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        // 1. Fetch Current User
        const userRes = await userService.getUserProfile();
        let myId = '';
        let myProfile = null;
        if (userRes.success && userRes.data) {
          myId = userRes.data.userProfile.userId;
          myProfile = userRes.data.userProfile;
          setCurrentUserId(myId);
        }

        // 2. Fetch Contract
        const res = await contractService.getContract(id);
        if (res.success && res.data) {
          const { contract: rawContract, milestones: rawMilestones } = res.data;
          setMilestones(rawMilestones);

          // 3. Construct ContractWithUsers
          const isClient = rawContract.client_user_id === myId;
          const isCreator = rawContract.creator_user_id === myId;
          const otherPartyId = isClient ? rawContract.creator_user_id : rawContract.client_user_id;

          // Fetch other party's avatar using batch API
          let otherPartyInfo = {
            avatarUrl: '',
            nickname: isClient ? 'Creator User' : 'Client User'
          };

          if (otherPartyId) {
            const avatarRes = await userService.getAvatarsBatch([otherPartyId]);
            if (avatarRes.success && avatarRes.data?.avatars && avatarRes.data.avatars[otherPartyId]) {
              otherPartyInfo = avatarRes.data.avatars[otherPartyId];
            }
          }

          const clientInfo = isClient && myProfile ? {
            user_id: myId,
            nickname: myProfile.displayName || myProfile.username,
            avatar: resolveAvatarUrl(myProfile.avatarUrl),
            role: 'Client'
          } : {
            user_id: rawContract.client_user_id,
            nickname: otherPartyInfo.nickname,
            avatar: resolveAvatarUrl(otherPartyInfo.avatarUrl),
            role: 'Client'
          };

          const creatorInfo = isCreator && myProfile ? {
            user_id: myId,
            nickname: myProfile.displayName || myProfile.username,
            avatar: resolveAvatarUrl(myProfile.avatarUrl),
            role: 'Creator'
          } : {
            user_id: rawContract.creator_user_id,
            nickname: otherPartyInfo.nickname,
            avatar: resolveAvatarUrl(otherPartyInfo.avatarUrl),
            role: 'Creator'
          };

          setContract({
            ...rawContract,
            client: clientInfo as any,
            creator: creatorInfo as any
          });
        } else {
          setError(res.error || '合同加载失败');
        }
      } catch (err) {
        console.error('Error fetching contract:', err);
        setError('合同加载失败');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Fetch reviews when contract is loaded
  useEffect(() => {
    if (!id) return;
    reviewService.getContractReviews(id).then(async res => {
      if (res.success && res.data?.reviews) {
        setReviews(res.data.reviews);
        const userIds = res.data.reviews.map((r: Review) => r.reviewerUserId);
        if (userIds.length > 0) {
          const avatarRes = await userService.getAvatarsBatch(userIds);
          if (avatarRes.success && avatarRes.data?.avatars) {
            setReviewerInfo(avatarRes.data.avatars);
          }
        }
      }
    }).catch(() => { });
  }, [id]);

  // Load pending milestone extension amendments. Re-run when contract reloads.
  const loadAmendments = async () => {
    if (!id) return;
    try {
      const res = await contractService.getAmendments(id);
      if (res.success) {
        const data = (res.data as any)?.amendments || (res.data as any)?.data?.amendments || [];
        const map: Record<string, any> = {};
        for (const a of data) {
          if (a.amendmentType !== 'EXTEND_DEADLINE') continue;
          if (a.status !== 'PENDING') continue;
          const milestoneId = a.changes?.milestoneId;
          if (!milestoneId) continue;
          map[milestoneId] = {
            amendmentId: a.amendmentId,
            proposedBy: a.proposedBy,
            oldCompletionDate: Number(a.changes?.oldCompletionDate) || 0,
            newCompletionDate: Number(a.changes?.newCompletionDate) || 0,
            reason: a.changes?.reason,
            createdAt: a.createdAt,
          };
        }
        setPendingExtensions(map);
      }
    } catch (e) {
      console.warn('Load amendments failed:', e);
    }
  };

  useEffect(() => { loadAmendments(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load contract-scoped disputes (申诉)
  const loadDisputes = async () => {
    if (!id) return;
    setDisputesLoading(true);
    try {
      const res = await disputeService.getByContract(id);
      if (res.success) {
        const list = (res.data as any)?.disputes || (res.data as any)?.data?.disputes || [];
        setDisputes(list);
      }
    } catch (e) {
      console.warn('Load disputes failed:', e);
    }
    setDisputesLoading(false);
  };

  useEffect(() => { loadDisputes(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // If the user is on the disputes tab but there are no disputes, fall back
  // to milestones (the tab won't render anyway).
  useEffect(() => {
    if (!disputesLoading && activeTab === 'disputes' && disputes.length === 0) {
      setActiveTab('milestones');
    }
  }, [activeTab, disputes.length, disputesLoading]);

  const activeDispute = disputes.find(d => d.status === 'PENDING' || d.status === 'IN_MEDIATION');
  const canFileDispute = !activeDispute;

  const handleWithdrawDispute = async (disputeId: string) => {
    const result = await confirm({
      title: '撤回申诉',
      message: '确定撤回此申诉吗？撤回后如需再次申诉需要重新提交。',
      confirmText: '撤回申诉',
      cancelText: '再想想',
      tone: 'danger',
    });
    if (result.action !== 'confirm') return;
    try {
      const res = await disputeService.withdraw(disputeId);
      if (res.success) {
        showToast('申诉已撤回', 'success');
        await loadDisputes();
      } else {
        showToast((res as any).error || '撤回失败', 'error');
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || '撤回失败', 'error');
    }
  };

  const openExtensionModal = (milestoneId: string) => {
    const m = milestones.find(x => x.milestone_id === milestoneId);
    if (!m) return;
    setExtensionModalMilestoneId(milestoneId);
    // Default suggestion: extend by 7 days
    const suggested = new Date((m.estimated_completion_date || Date.now()) + 7 * 24 * 3600_000);
    setExtensionNewDate(suggested.toISOString().slice(0, 10));
    setExtensionReason('');
  };

  const closeExtensionModal = () => {
    setExtensionModalMilestoneId(null);
    setExtensionNewDate('');
    setExtensionReason('');
  };

  const handleSubmitExtension = async () => {
    if (!id || !extensionModalMilestoneId) return;
    if (!extensionNewDate || !extensionReason.trim()) {
      showToast('请填写完整', 'error');
      return;
    }
    setExtensionSubmitting(true);
    try {
      const newTs = new Date(extensionNewDate).getTime();
      const res = await contractService.requestMilestoneExtension(
        id,
        extensionModalMilestoneId,
        newTs,
        extensionReason.trim(),
      );
      if (res.success) {
        showToast('延期申请已提交，等待对方批准', 'success');
        closeExtensionModal();
        await loadAmendments();
      } else {
        showToast((res as any).error || '申请失败', 'error');
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || '申请失败', 'error');
    }
    setExtensionSubmitting(false);
  };

  const handleApproveExtension = async (amendmentId: string) => {
    if (!window.confirm('确定批准此延期申请？批准后里程碑完成日期将更新。')) return;
    try {
      const res = await contractService.reviewAmendment(amendmentId, true);
      if (res.success) {
        showToast('已批准延期', 'success');
        setTimeout(() => window.location.reload(), 600);
      } else {
        showToast((res as any).error || '操作失败', 'error');
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || '操作失败', 'error');
    }
  };

  const handleRejectExtension = async (amendmentId: string) => {
    const note = window.prompt('拒绝理由（可选）：') || undefined;
    try {
      const res = await contractService.reviewAmendment(amendmentId, false, note);
      if (res.success) {
        showToast('已拒绝延期', 'success');
        await loadAmendments();
      } else {
        showToast((res as any).error || '操作失败', 'error');
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || '操作失败', 'error');
    }
  };

  const progress = useMemo(() => {
    return calculateContractProgress(milestones);
  }, [milestones]);

  // Construct initial data for form
  const editInitialData: Partial<CreateContractInput> | undefined = useMemo(() => {
    if (!contract) return undefined;
    return {
      title: contract.title,
      description: contract.description,
      requirements: contract.requirements,
      terms: contract.terms,
      total_amount: contract.total_amount,
      currency: contract.currency,
      estimated_delivery_date: contract.estimated_delivery_date,
      milestones: milestones.map(m => ({
        title: m.title,
        description: m.description,
        order_index: m.order_index,
        payment_amount: m.payment_amount,
        requires_deliverable: m.requires_deliverable,
        has_penalty: m.has_penalty,
        penalty_type: m.penalty_type,
        penalty_value: m.penalty_value,
        penalty_description: m.penalty_description,
        estimated_completion_date: m.estimated_completion_date,
      })),
      client_user_id: contract.client_user_id,
      brief_id: contract.brief_id,
      custom_pdf_oss_key: contract.custom_pdf_oss_key
    };
  }, [contract, milestones]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">加载中...</div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-slate-900 mb-2">合同不存在</h2>
          <p className="text-slate-600 mb-6">未找到该合同信息</p>
          <Button variant="primary" onClick={() => {
            const view = searchParams.get('view') || 'client';
            navigate(`/contracts?view=${view}`);
          }}>
            返回合同列表
          </Button>
        </div>
      </div>
    );
  }

  const role = getUserRole(contract, currentUserId);
  const otherParty = role === 'client' ? contract.creator : contract.client;

  const handleStartMilestone = async (milestoneId: string) => {
    if (!contract) return;
    try {
      const res = await contractService.startMilestone(contract.contract_id, milestoneId);
      if (res.success) {
        showToast('里程碑已开始', 'success');
        setTimeout(() => window.location.reload(), 1000);
      } else {
        showToast(res.error || '操作失败', 'error');
      }
    } catch (error) {
      console.error('Start milestone error:', error);
      showToast('操作失败', 'error');
    }
  };

  const handleSubmitDeliverable = (milestoneId: string) => {
    const milestone = milestones.find((m) => m.milestone_id === milestoneId);
    if (milestone) {
      setSelectedMilestone(milestone);
      setShowDeliverableModal(true);
    }
  };

  const handleReviewMilestone = (milestoneId: string) => {
    const milestone = milestones.find((m) => m.milestone_id === milestoneId);
    if (milestone) {
      setSelectedMilestone(milestone);
      setShowRevisionModal(true);
    }
  };

  const handleDeliverableSubmit = async (milestoneId: string, deliverables: string[], notes?: string) => {
    if (!contract) return;
    try {
      // Note: Backend submitMilestone accepts deliverableUrls. Passing notes isn't supported yet in backend or service type
      const res = await contractService.submitMilestone(contract.contract_id, milestoneId, deliverables);
      if (res.success) {
        showToast('交付物提交成功', 'success');
        setShowDeliverableModal(false);
        setTimeout(() => window.location.reload(), 1000);
      } else {
        showToast(res.error || '提交失败', 'error');
      }
    } catch (error) {
      console.error('Submit deliverable error:', error);
      showToast('提交失败', 'error');
    }
  };

  const handleApproveMilestone = async (milestoneId: string) => {
    if (!contract) return;
    try {
      const res = await contractService.approveMilestone(contract.contract_id, milestoneId);
      if (res.success) {
        showToast('里程碑已批准', 'success');
        setShowRevisionModal(false);
        setTimeout(() => window.location.reload(), 1000);
      } else {
        showToast(res.error || '操作失败', 'error');
      }
    } catch (error) {
      console.error('Approve milestone error:', error);
      showToast('操作失败', 'error');
    }
  };

  const handleRequestRevision = async (milestoneId: string, feedback: string) => {
    if (!contract) return;
    try {
      const res = await contractService.rejectMilestone(contract.contract_id, milestoneId, feedback);
      if (res.success) {
        showToast('已请求修改', 'success');
        setShowRevisionModal(false);
        setTimeout(() => window.location.reload(), 1000);
      } else {
        showToast(res.error || '操作失败', 'error');
      }
    } catch (error) {
      console.error('Request revision error:', error);
      showToast('操作失败', 'error');
    }
  };

  const handleCancelMilestone = async (milestoneId: string) => {
    if (!contract) return;
    if (window.confirm('确定要取消此里程碑吗？')) {
      try {
        const res = await contractService.cancelMilestone(contract.contract_id, milestoneId);
        if (res.success) {
          showToast('里程碑已取消', 'success');
          setTimeout(() => window.location.reload(), 1000);
        } else {
          showToast(res.error || '操作失败', 'error');
        }
      } catch (error) {
        console.error('Cancel milestone error:', error);
        showToast('操作失败', 'error');
      }
    }
  };

  const handleAcceptContract = () => {
    setActionType('ACCEPT');
    setShowActionModal(true);
  };

  const handleRejectContract = () => {
    setActionType('REJECT');
    setShowActionModal(true);
  };

  const handleCancelContract = () => {
    setShowSettingsMenu(false);
    setActionType('CANCEL');
    setShowActionModal(true);
  };

  const handleActionConfirm = async (reason?: string) => {
    if (!contract) return;

    try {
      let res;
      switch (actionType) {
        case 'ACCEPT':
          res = await contractService.acceptContract(contract.contract_id);
          break;
        case 'REJECT':
          res = await contractService.rejectContract(contract.contract_id);
          break;
        case 'CANCEL':
          res = await contractService.cancelContract(contract.contract_id, reason || 'Cancelled by user');
          break;
      }

      const actionLabel = actionType === 'ACCEPT' ? '接受'
        : actionType === 'REJECT' ? '拒绝'
        : actionType === 'CANCEL' ? '取消'
        : '操作';
      if (res && res.success) {
        showToast(`${actionLabel}成功`, 'success');
        // Refresh data
        setTimeout(() => window.location.reload(), 1000);
      } else {
        showToast(`${actionLabel}失败：${res?.error || '未知错误'}`, 'error');
      }
    } catch (error) {
      console.error('Contract action error:', error);
      showToast('操作失败', 'error');
    }
  };

  const handleEditContract = () => {
    setShowSettingsMenu(false);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (data: CreateContractInput, isDraft: boolean = false) => {
    if (!contract) return;
    try {
      const payload = { ...data, status: (isDraft ? 'DRAFT' : 'PENDING_CLIENT_APPROVAL') as ContractStatus };
      const res = await contractService.updateContract(contract.contract_id, payload);
      if (res.success) {
        showToast(isDraft ? '草稿已更新' : '合同已更新并提交审核', 'success');
        setIsEditModalOpen(false);
        setTimeout(() => window.location.reload(), 1000);
      } else {
        showToast(res.error || '更新失败', 'error');
      }
    } catch (error) {
      console.error('Update contract error:', error);
      showToast('更新失败', 'error');
    }
  };



  const handleDeleteDraft = async () => {
    if (!contract) return;
    if (window.confirm('确定要删除草稿吗？此操作无法撤销。')) {
      try {
        const res = await contractService.deleteContract(contract.contract_id);
        if (res.success) {
          showToast('草稿已删除', 'success');
          navigate('/contracts?view=creator');
        } else {
          showToast(res.error || '删除失败', 'error');
        }
      } catch (error) {
        console.error('Delete draft error:', error);
        showToast('删除失败', 'error');
      }
    }
  };

  const handleSubmitDraft = async () => {
    if (!contract) return;
    try {
      const res = await contractService.publishContract(contract.contract_id);
      if (res.success) {
        showToast('合同已提交审核', 'success');
        setTimeout(() => window.location.reload(), 1000);
      } else {
        showToast(res.error || '提交失败', 'error');
      }
    } catch (error) {
      console.error('Submit draft error:', error);
      showToast('提交失败', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <ToastWrapper />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-24 pb-20">
        {/* Back Button */}
        <button
          onClick={() => {
            // Preserve the view mode when navigating back
            const view = searchParams.get('view') || 'client';
            navigate(`/contracts?view=${view}`);
          }}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">返回合同列表</span>
        </button>

        {/* Header */}
        <div className="bg-white rounded-2xl border border-slate-200 p-8 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <StatusBadge config={contractStatusConfig[contract.status]} size="md" />
                {contract.brief_id && (
                  <button
                    onClick={async () => {
                      try {
                        const res = await briefService.getBriefById(contract.brief_id!);
                        const raw = res.data as any;
                        const brief = raw?.data?.brief || raw?.brief || raw;
                        if (brief) setSelectedBrief(brief);
                      } catch (e) { console.error('Failed to load brief:', e); }
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    Brief #{contract.brief_id.slice(-6)}
                    <ExternalLink className="w-3 h-3" />
                  </button>
                )}
              </div>
              <h1 className="text-3xl font-semibold text-slate-900 mb-2">{contract.title}</h1>
              <p className="text-slate-600">{contract.description}</p>
            </div>

            {/* Settings Dropdown */}
            {(role === 'client' || role === 'creator') && (
              <div className="relative" ref={settingsMenuRef}>
                <button
                  onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                  className="ml-4 p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  aria-label="设置"
                >
                  <MoreVertical className="w-5 h-5 text-slate-600" />
                </button>

                {showSettingsMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10 animate-in fade-in zoom-in-95 duration-200">
                    {canFileDispute ? (
                      <button
                        onClick={() => {
                          setShowSettingsMenu(false);
                          setShowDisputeModal(true);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                      >
                        <Flag className="w-4 h-4 text-amber-500" />
                        发起申诉
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setShowSettingsMenu(false);
                          setActiveTab('disputes');
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-slate-500 hover:bg-slate-50 flex items-center gap-2"
                      >
                        <Flag className="w-4 h-4 text-amber-500" />
                        查看申诉进度
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowSettingsMenu(false);
                        setShowSupportModal(true);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <MessageSquare className="w-4 h-4 text-blue-500" />
                      联系人工客服
                    </button>

                    {role === 'creator' && contract.status === 'DRAFT' ? (
                      <>
                        <button
                          onClick={handleEditContract}
                          className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 border-t border-slate-50 mt-1 pt-2"
                        >
                          <Edit className="w-4 h-4" />
                          编辑合同
                        </button>
                        <button
                          onClick={handleSubmitDraft}
                          className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                        >
                          <Send className="w-4 h-4 text-green-500" />
                          提交审核
                        </button>
                        <button
                          onClick={handleDeleteDraft}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          删除草稿
                        </button>
                      </>
                    ) : (
                      <>
                        {canEditContract(contract, currentUserId) && (
                          <button
                            onClick={handleEditContract}
                            className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 border-t border-slate-50 mt-1 pt-2"
                          >
                            <Edit className="w-4 h-4" />
                            编辑合同
                          </button>
                        )}
                        {canCancelContract(contract, currentUserId) && (
                          <button
                            onClick={handleCancelContract}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            取消合同
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Parties and Key Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Client */}
            <div className="flex items-center gap-3">
              <span
                className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate(`/profile/${contract.client.user_id}`)}
              >
                <img
                  src={contract.client.avatar}
                  alt={contract.client.nickname}
                  className="w-12 h-12 rounded-full border-2 border-slate-100"
                />
                <div>
                  <p className="font-semibold text-slate-900">{contract.client.nickname}</p>
                  <p className="text-xs text-slate-500">客户</p>
                </div>
              </span>
            </div>

            {/* Creator */}
            <div className="flex items-center gap-3">
              <span
                className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate(`/profile/${contract.creator.user_id}`)}
              >
                <img
                  src={contract.creator.avatar}
                  alt={contract.creator.nickname}
                  className="w-12 h-12 rounded-full border-2 border-slate-100"
                />
                <div>
                  <p className="font-semibold text-slate-900">{contract.creator.nickname}</p>
                  <p className="text-xs text-slate-500">创作者</p>
                </div>
              </span>
            </div>

            {/* Key Metrics */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-600">总金额：</span>
                <span className="font-semibold text-slate-900">
                  {contract.currency === 'CNY' ? '¥' : '$'}
                  {contract.total_amount.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-600">交付日期：</span>
                <span className="font-semibold text-slate-900">
                  {formatDate(contract.estimated_delivery_date)}
                </span>
              </div>
            </div>
          </div>

          {/* Signing Actions */}
          {contract.status === 'PENDING_CLIENT_APPROVAL' && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              {contract.esign_flow_id ? (
                <div className="space-y-4">
                  {/* Signing status - vertical layout */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-2 text-sm">
                      <div className={`flex items-center gap-2 ${contract.client_sign_status === 'signed' ? 'text-green-600' : 'text-orange-500'}`}>
                        <span className="text-slate-600 font-medium">甲方：</span>
                        <span>{contract.client_sign_status === 'signed' ? '✅ 已签署' : '⏳ 待签署'}</span>
                      </div>
                      <div className={`flex items-center gap-2 ${contract.creator_sign_status === 'signed' ? 'text-green-600' : 'text-orange-500'}`}>
                        <span className="text-slate-600 font-medium">乙方：</span>
                        <span>{contract.creator_sign_status === 'signed' ? '✅ 已签署' : '⏳ 待签署'}</span>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        if (refreshingStatus) return;
                        setRefreshingStatus(true);
                        try {
                          const res = await contractService.refreshSignStatus(contract.contract_id);
                          const data = (res.data as any)?.data || res.data;
                          if (data) {
                            setContract((prev) => prev ? {
                              ...prev,
                              client_sign_status: data.clientSignStatus || prev.client_sign_status,
                              creator_sign_status: data.creatorSignStatus || prev.creator_sign_status,
                              status: data.contract?.status || prev.status,
                            } : prev);
                            showToast('状态已刷新', 'success');
                          }
                        } catch { showToast('刷新失败', 'error'); }
                        setRefreshingStatus(false);
                      }}
                      disabled={refreshingStatus}
                      className="text-xs text-slate-500 hover:text-slate-700 disabled:opacity-50 flex items-center gap-1.5 mt-1 shrink-0"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${refreshingStatus ? 'animate-spin' : ''}`} />
                      {refreshingStatus ? '刷新中...' : '刷新状态'}
                    </button>
                  </div>

                  {/* Sign button for current user */}
                  {((currentUserId === contract.client_user_id && contract.client_sign_status !== 'signed') ||
                    (currentUserId === contract.creator_user_id && contract.creator_sign_status !== 'signed')) && (
                      <Button variant="primary" size="lg" onClick={() => setShowSignMethodModal(true)}>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        签署合同
                      </Button>
                    )}
                </div>
              ) : (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
                  <div className="flex items-start gap-2">
                    <span className="text-amber-600">⚠️</span>
                    <div className="flex-1 text-sm">
                      <p className="font-medium text-amber-900">签署流程未创建</p>
                      <p className="text-amber-700 mt-1">可能原因：双方未完成实人认证，或 e签宝 服务异常。合同签署需要双方均通过实名认证。</p>
                    </div>
                  </div>
                  <Button variant="secondary" size="sm" onClick={async () => {
                    try {
                      const res = await contractService.createSigningFlow(contract.contract_id);
                      if (res.success) { showToast('签署流程已创建', 'success'); window.location.reload(); }
                      else showToast((res as any).error || '创建签署流程失败', 'error');
                    } catch (err: any) {
                      showToast(err?.response?.data?.error || '创建签署流程失败', 'error');
                    }
                  }}>
                    重新发起签署
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Download signed PDF */}
          {contract.status === 'ACTIVE' && contract.esign_flow_id && (
            <div className="mt-4">
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await contractService.getSignedPdfUrl(contract.contract_id);
                    const data = (res.data as any)?.data || res.data;
                    if (data?.downloadUrl) window.open(data.downloadUrl, '_blank');
                    else showToast('获取签署文件失败', 'error');
                  } catch { showToast('获取签署文件失败', 'error'); }
                }}
                className="inline-flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                下载合同
              </Button>
            </div>
          )}

        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div className="bg-white rounded-2xl border border-slate-200">
              <div className="border-b border-slate-200">
                <div className="flex">
                  <button
                    className={`flex-1 px-6 py-4 font-medium transition-colors relative ${activeTab === 'milestones'
                      ? 'text-blue-600'
                      : 'text-slate-600 hover:text-slate-900'
                      }`}
                    onClick={() => setActiveTab('milestones')}
                  >
                    里程碑
                    {activeTab === 'milestones' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                    )}
                  </button>
                  <button
                    className={`flex-1 px-6 py-4 font-medium transition-colors relative ${activeTab === 'details'
                      ? 'text-blue-600'
                      : 'text-slate-600 hover:text-slate-900'
                      }`}
                    onClick={() => setActiveTab('details')}
                  >
                    合同详情
                    {activeTab === 'details' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                    )}
                  </button>
                  {contract.status === 'COMPLETED' && (
                    <button
                      className={`flex-1 px-6 py-4 font-medium transition-colors relative ${activeTab === 'reviews'
                        ? 'text-blue-600'
                        : 'text-slate-600 hover:text-slate-900'
                        }`}
                      onClick={() => setActiveTab('reviews')}
                    >
                      评价
                      {reviews.length > 0 && <span className="ml-1.5 text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">{reviews.length}</span>}
                      {activeTab === 'reviews' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                      )}
                    </button>
                  )}
                  {disputes.length > 0 && (
                    <button
                      className={`flex-1 px-6 py-4 font-medium transition-colors relative ${activeTab === 'disputes'
                        ? 'text-blue-600'
                        : 'text-slate-600 hover:text-slate-900'
                        }`}
                      onClick={() => setActiveTab('disputes')}
                    >
                      申诉
                      <span className="ml-1.5 text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">{disputes.length}</span>
                      {activeDispute && (
                        <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-amber-500 align-middle" />
                      )}
                      {activeTab === 'disputes' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="p-6">
                {activeTab === 'milestones' && (
                  <MilestoneTimeline
                    milestones={milestones}
                    contract={contract}
                    currentUserId={currentUserId}
                    onStartMilestone={handleStartMilestone}
                    onSubmitDeliverable={handleSubmitDeliverable}
                    onApproveMilestone={handleReviewMilestone}
                    onRequestRevision={handleReviewMilestone}
                    onCancelMilestone={handleCancelMilestone}
                    pendingExtensionsByMilestoneId={pendingExtensions}
                    onRequestExtension={openExtensionModal}
                    onApproveExtension={handleApproveExtension}
                    onRejectExtension={handleRejectExtension}
                  />
                )}

                {activeTab === 'details' && (
                  <div className="space-y-6">
                    {/* Requirements */}
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-3">项目要求</h3>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">
                          {contract.requirements}
                        </p>
                      </div>
                    </div>

                    {/* Terms */}
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-3">合同条款</h3>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">
                          {contract.terms}
                        </p>
                      </div>
                    </div>

                    {/* Timestamps */}
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-3">时间线</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">创建时间：</span>
                          <span className="text-slate-900">{formatDateTime(contract.created_at)}</span>
                        </div>
                        {contract.client_signed_at && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">客户签署：</span>
                            <span className="text-slate-900">{formatDateTime(contract.client_signed_at)}</span>
                          </div>
                        )}
                        {contract.creator_signed_at && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">创作者签署：</span>
                            <span className="text-slate-900">{formatDateTime(contract.creator_signed_at)}</span>
                          </div>
                        )}
                        {contract.completed_at && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">完成时间：</span>
                            <span className="text-slate-900">{formatDateTime(contract.completed_at)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'reviews' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-slate-900">合作评价</h3>
                      {!reviews.find(r => r.reviewerUserId === currentUserId) && (
                        <button
                          onClick={() => setShowReviewModal(true)}
                          className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800"
                        >
                          写评价
                        </button>
                      )}
                    </div>
                    {reviews.length === 0 ? (
                      <div className="py-10 text-center text-gray-400 text-sm">
                        暂无评价，完成合作后可以互相评价
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {reviews.map((r) => (
                          <ReviewCard
                            key={r.contractId + r.reviewerUserId}
                            review={r}
                            reviewerName={
                              r.reviewerUserId === contract.client?.user_id ? contract.client?.nickname :
                                r.reviewerUserId === contract.creator?.user_id ? contract.creator?.nickname :
                                  reviewerInfo[r.reviewerUserId]?.nickname
                            }
                            reviewerAvatar={
                              r.reviewerUserId === contract.client?.user_id ? contract.client?.avatar :
                                r.reviewerUserId === contract.creator?.user_id ? contract.creator?.avatar :
                                  reviewerInfo[r.reviewerUserId]?.avatarUrl
                            }
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'disputes' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-slate-900">合同申诉</h3>
                      {canFileDispute && contract.status !== 'CANCELLED' && (
                        <button
                          onClick={() => setShowDisputeModal(true)}
                          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                        >
                          发起申诉
                        </button>
                      )}
                    </div>
                    {!canFileDispute && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        当前合同已有进行中的申诉，平台介入后才能发起新的申诉。
                      </div>
                    )}
                    {disputesLoading ? (
                      <div className="py-10 text-center text-gray-400 text-sm">加载中...</div>
                    ) : disputes.length === 0 ? (
                      <div className="py-10 text-center text-gray-400 text-sm">
                        暂无申诉记录。如果您认为合同执行存在问题，可点击右上角发起申诉。
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {disputes.map(d => {
                          const isMine = d.initiatorUserId === currentUserId;
                          const STATUS_CN: Record<string, { label: string; cls: string }> = {
                            PENDING:      { label: '待处理',  cls: 'bg-yellow-100 text-yellow-800' },
                            IN_MEDIATION: { label: '调解中',  cls: 'bg-purple-100 text-purple-800' },
                            RESOLVED:     { label: '已处理',  cls: 'bg-green-100 text-green-800' },
                            REJECTED:     { label: '未支持',  cls: 'bg-red-100 text-red-800' },
                            WITHDRAWN:    { label: '已撤回',  cls: 'bg-slate-100 text-slate-700' },
                          };
                          const REASON_CN: Record<string, string> = {
                            quality: '交付质量', scope: '范围争议', payment: '付款争议',
                            communication: '沟通失联', other: '其他',
                          };
                          const EXPECT_CN: Record<string, string> = {
                            full_refund: '全额退款', partial_refund: '部分退款',
                            redo: '重新交付', release_payment: '释放款项', other: '其他',
                          };
                          const s = STATUS_CN[d.status] || { label: d.status, cls: 'bg-slate-100 text-slate-700' };
                          const canWithdraw = isMine && d.status === 'PENDING';
                          return (
                            <div key={d.disputeId} className="rounded-lg border border-slate-200 bg-white p-4">
                              <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="flex items-center gap-2 text-sm">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.cls}`}>{s.label}</span>
                                  <span className="text-slate-500">{isMine ? '由您发起' : '对方发起'}</span>
                                  <span className="text-slate-400 text-xs">·</span>
                                  <span className="text-slate-500 text-xs">{new Date(Number(d.createdAt)).toLocaleString('zh-CN')}</span>
                                </div>
                                {canWithdraw && (
                                  <button
                                    onClick={() => handleWithdrawDispute(d.disputeId)}
                                    className="text-xs px-3 py-1 rounded border border-slate-200 text-slate-700 hover:bg-slate-50"
                                  >
                                    撤回申诉
                                  </button>
                                )}
                              </div>
                              <dl className="space-y-2 text-sm">
                                <div className="flex">
                                  <dt className="w-24 text-slate-500 shrink-0">原因</dt>
                                  <dd className="text-slate-900">{REASON_CN[d.reason] || d.reason}</dd>
                                </div>
                                <div className="flex">
                                  <dt className="w-24 text-slate-500 shrink-0">诉求</dt>
                                  <dd className="text-slate-900">{EXPECT_CN[d.expectation] || d.expectation}</dd>
                                </div>
                                <div className="flex">
                                  <dt className="w-24 text-slate-500 shrink-0">陈述</dt>
                                  <dd className="text-slate-700 whitespace-pre-wrap">{d.description}</dd>
                                </div>
                                {d.evidenceUrls && d.evidenceUrls.length > 0 && (
                                  <div className="flex">
                                    <dt className="w-24 text-slate-500 shrink-0">证据</dt>
                                    <dd className="flex flex-wrap gap-2">
                                      {d.evidenceUrls.map((url: string, i: number) => (
                                        <a key={i} href={url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">
                                          文件 {i + 1}
                                        </a>
                                      ))}
                                    </dd>
                                  </div>
                                )}
                              </dl>
                              {(d.status === 'RESOLVED' || d.status === 'REJECTED') && d.adminNote && (
                                <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 p-3 text-sm">
                                  <div className="text-xs font-medium text-slate-600 mb-1">平台处理意见</div>
                                  <div className="text-slate-800 whitespace-pre-wrap">{d.adminNote}</div>
                                  {d.reviewedBy && d.reviewedAt && (
                                    <div className="text-xs text-slate-400 mt-1">
                                      由 {d.reviewedBy} 于 {new Date(Number(d.reviewedAt)).toLocaleString('zh-CN')} 处理
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            <ProgressMetrics progress={progress} currency={contract.currency} />
            <PaymentEscrowPanel
              contract={contract}
              milestones={milestones}
              currentUserId={currentUserId}
              onPaid={() => setTimeout(() => window.location.reload(), 800)}
            />
            <InvoiceSection
              contract={contract}
              currentUserId={currentUserId}
              showToast={showToast}
              onUploaded={() => setTimeout(() => window.location.reload(), 800)}
            />
          </div>
        </div>
      </div>


      {/* Modals */}
      {selectedMilestone && showDeliverableModal && (
        <DeliverableSubmission
          isOpen={showDeliverableModal}
          onClose={() => {
            setShowDeliverableModal(false);
            setSelectedMilestone(null);
          }}
          milestone={selectedMilestone}
          onSubmit={handleDeliverableSubmit}
        />
      )}

      {selectedMilestone && showRevisionModal && (
        <RevisionRequest
          isOpen={showRevisionModal}
          onClose={() => {
            setShowRevisionModal(false);
            setSelectedMilestone(null);
          }}
          milestone={selectedMilestone}
          onApprove={handleApproveMilestone}
          onRequestRevision={handleRequestRevision}
        />
      )}

      <ContractActionModal
        isOpen={showActionModal}
        onClose={() => setShowActionModal(false)}
        onConfirm={handleActionConfirm}
        type={actionType}
        title={
          actionType === 'ACCEPT' ? '接受合同' :
            actionType === 'REJECT' ? '拒绝合同' : '取消合同'
        }
        contractTitle={contract?.title || ''}
      />

      {/* Dispute Modal */}
      {contract && (
        <DisputeModal
          isOpen={showDisputeModal}
          onClose={() => setShowDisputeModal(false)}
          onSubmit={async (data) => {
            try {
              const res = await disputeService.create({
                contractId: contract.contract_id,
                reason: data.reason,
                expectation: data.expectation,
                description: data.description,
                // Files attachment is not yet wired through OSS — skip for now.
              });
              if (res.success) {
                const created = (res.data as any)?.dispute || (res.data as any)?.data?.dispute;
                setShowDisputeModal(false);
                showToast('申诉已提交，平台客服将尽快介入', 'success');
                if (created) {
                  // Optimistic insert so the tab is visible even if the
                  // contract-scoped list endpoint hasn't been deployed yet.
                  setDisputes(prev => [created, ...prev.filter(d => d.disputeId !== created.disputeId)]);
                }
                setActiveTab('disputes');
                await loadDisputes();
              } else {
                showToast((res as any).error || '提交失败，请重试', 'error');
              }
            } catch (err: any) {
              showToast(err?.response?.data?.error || '提交失败，请重试', 'error');
            }
          }}
          contractTitle={contract.title}
        />
      )}

      {/* Support Contact Modal */}
      <Modal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        title="联系人工客服"
        size="sm"
      >
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-blue-600" />
          </div>
          <p className="text-slate-600 mb-2">遇到问题需要协助？请拨打我们的客服热线：</p>
          <p className="text-2xl font-bold text-slate-900 mb-6">400-123-4567</p>
          <p className="text-sm text-slate-500">
            工作时间：周一至周日 9:00 - 21:00
          </p>
        </div>
        <div className="mt-6 flex justify-center">
          <Button variant="primary" onClick={() => setShowSupportModal(false)} className="w-full">
            知道了
          </Button>
        </div>
      </Modal>

      {/* Edit Contract Modal */}
      {contract && (
        <CreateContractModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleEditSubmit}
          initialData={editInitialData}
          mode="edit"
        />
      )}

      {/* Review Modal */}
      {contract && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          contractId={contract.contract_id}
          revieweeName={role === 'creator' ? (contract.client?.nickname || '') : (contract.creator?.nickname || '')}
          role={role === 'creator' ? 'creator_to_client' : 'client_to_creator'}
          onSubmitted={() => {
            reviewService.getContractReviews(contract.contract_id).then(res => {
              if (res.success && res.data?.reviews) setReviews(res.data.reviews);
            });
          }}
        />
      )}
      {selectedBrief && (
        <BriefDetailModal
          isOpen={!!selectedBrief}
          onClose={() => setSelectedBrief(null)}
          brief={selectedBrief}
        />
      )}

      {/* Sign Method Modal */}
      {showSignMethodModal && contract && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowSignMethodModal(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">选择签署方式</h3>
            <p className="text-sm text-slate-500 mb-5">您可以通过以下任一方式完成合同签署：</p>

            <div className="space-y-3">
              <button
                onClick={async () => {
                  try {
                    const res = await contractService.getSignUrl(contract.contract_id, window.location.href);
                    const data = (res.data as any)?.data || res.data;
                    if (data?.signUrl) {
                      window.open(data.signUrl, '_blank');
                      setShowSignMethodModal(false);
                    } else showToast('获取签署链接失败', 'error');
                  } catch { showToast('获取签署链接失败', 'error'); }
                }}
                className="w-full p-4 border border-slate-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left flex items-start gap-3"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">🖥️</div>
                <div className="flex-1">
                  <div className="font-medium text-slate-900">网页端签署</div>
                  <div className="text-xs text-slate-500 mt-1">打开 e签宝 网页，使用手机号 + 短信验证码登录后在线签署</div>
                </div>
              </button>

              <div className="w-full p-4 border border-slate-200 rounded-xl bg-slate-50 flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center shrink-0">📱</div>
                <div className="flex-1">
                  <div className="font-medium text-slate-900">短信签署</div>
                  <div className="text-xs text-slate-500 mt-1">系统会向您注册的手机号发送短信，点击链接可跳转到支付宝小程序完成签署</div>
                  <div className="text-xs text-amber-600 mt-2">💡 请查看您的手机短信</div>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowSignMethodModal(false)}
              className="mt-5 w-full py-2.5 text-sm text-slate-600 hover:text-slate-900"
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* Milestone extension request modal */}
      {extensionModalMilestoneId && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-900">申请延期</h3>
              <button onClick={closeExtensionModal} className="p-1 rounded hover:bg-slate-100">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-600">
                延期申请提交后将通知对方。批准后里程碑的完成日期会更新；拒绝后里程碑保持原日期。
              </p>
              {(() => {
                const m = milestones.find(x => x.milestone_id === extensionModalMilestoneId);
                return m ? (
                  <div className="rounded-md bg-slate-50 p-3 text-sm space-y-1">
                    <div><span className="text-slate-500">里程碑：</span><span className="font-medium">{m.title}</span></div>
                    <div><span className="text-slate-500">原完成日期：</span>{new Date(m.estimated_completion_date).toLocaleDateString('zh-CN')}</div>
                  </div>
                ) : null;
              })()}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  新完成日期 <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={extensionNewDate}
                  onChange={(e) => setExtensionNewDate(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                  min={new Date().toISOString().slice(0, 10)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  延期理由 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={extensionReason}
                  onChange={(e) => setExtensionReason(e.target.value)}
                  rows={3}
                  placeholder="请说明延期原因，对方会看到此理由"
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 resize-none"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={closeExtensionModal} disabled={extensionSubmitting}>取消</Button>
                <Button onClick={handleSubmitExtension} disabled={extensionSubmitting}>
                  {extensionSubmitting ? '提交中...' : '提交申请'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractDetail;
